import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FeatureInfoComponent } from '@principle-theorem/ng-feature-flags';
import {
  ContentContainerComponent,
  CurrentBrandScope,
  InformationBoxComponent,
  OrganisationService,
  PracticeSelectorComponent,
} from '@principle-theorem/ng-principle-shared';
import { BreadcrumbsComponent } from '@principle-theorem/ng-shared';
import {
  asyncForEach,
  Firestore,
  isSameRef,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { type IPractice } from '@principle-theorem/principle-core/interfaces';
import { ReferralEmailDefaultsPracticeComponent } from './referral-email-defaults-practice/referral-email-defaults-practice.component';

@Component({
  selector: 'pr-referral-email-defaults',
  imports: [
    CommonModule,
    MatButtonModule,
    MatTooltipModule,
    BreadcrumbsComponent,
    FeatureInfoComponent,
    ContentContainerComponent,
    InformationBoxComponent,
    PracticeSelectorComponent,
    ReferralEmailDefaultsPracticeComponent,
  ],
  templateUrl: './referral-email-defaults.component.html',
  styleUrl: './referral-email-defaults.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralEmailDefaultsComponent {
  private _currentBrand = inject(CurrentBrandScope);
  readonly organisation = inject(OrganisationService);

  readonly brand = toSignal(this._currentBrand.doc$);
  readonly selectedPractice = signal<WithRef<IPractice> | undefined>(undefined);

  readonly breadcrumbs = computed(() => {
    const brand = this.brand();
    return brand
      ? [
          { label: 'Settings', path: '../../../' },
          { label: brand.name },
          { label: 'Referrals' },
        ]
      : [];
  });

  async applyToAll(): Promise<void> {
    const selected = this.selectedPractice();
    if (!selected) {
      return;
    }

    const practices = await snapshot(this.organisation.practices$);
    if (!practices || practices.length < 2) {
      return;
    }

    const defaults = selected.settings.referral?.emailTemplateDefaults;
    const otherPractices = practices.filter(
      (practice) => !isSameRef(practice.ref, selected.ref)
    );

    await asyncForEach(otherPractices, async (practice) => {
      await Firestore.patchDoc(practice.ref, {
        settings: {
          ...practice.settings,
          referral: {
            ...practice.settings.referral,
            emailTemplateDefaults: defaults,
          },
        },
      });
    });
  }
}
